import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import mobileMockup from '../assets/home-mobile-mockup.png'
import appStoreBadge from '../assets/home-promo-app-store.png'
import googlePlayBadge from '../assets/home-promo-google-play.png'
import rewardsIcon from '../assets/home-promo-rewards-icon.svg'
import { env } from '@/config/env'
import { captureSpMobileStoreBannerClicked } from '@/lib/analytics'
import { useSessionStore } from '@/stores/useSessionStore'
import {
  homePromoAppStoreImgSx,
  homePromoAppStoreLinkSx,
  homePromoBadgeSx,
  homePromoBadgeTextSx,
  homePromoFloatIconImgSx,
  homePromoFloatIconSx,
  homePromoFloatSx,
  homePromoGooglePlayImgSx,
  homePromoGooglePlayLinkSx,
  homePromoHeadingSx,
  homePromoPhoneImgSx,
  homePromoPhoneWrapSx,
  homePromoRewardsAmountSx,
  homePromoRewardsLabelSx,
  homePromoRootSx,
  homePromoStoreRowSx,
  homePromoSubtextSx,
} from '../styles/homePromoStyles'

/** Returns the best store URL for the current device. */
function resolveStoreUrl(googlePlayUrl: string, appStoreUrl: string): { google: string; apple: string } {
  const ua = globalThis.navigator?.userAgent ?? ''
  const isIos = /iphone|ipad|ipod/i.test(ua)
  const isAndroid = /android/i.test(ua)

  // On iOS, both buttons open App Store; on Android, both open Play Store.
  // On desktop / unknown, each button opens its own store.
  if (isIos) {
    return { google: appStoreUrl || '#', apple: appStoreUrl || '#' }
  }
  if (isAndroid) {
    return { google: googlePlayUrl || '#', apple: googlePlayUrl || '#' }
  }
  return { google: googlePlayUrl || '#', apple: appStoreUrl || '#' }
}

export function HomeMobilePromo() {
  const storeUrls = resolveStoreUrl(env.VITE_GOOGLE_PLAY_URL, env.VITE_APP_STORE_URL)

  const handleGooglePlay = (e: React.MouseEvent<HTMLAnchorElement>) => {
    captureSpMobileStoreBannerClicked({
      store_target: 'google_playstore',
      sp_user_id: useSessionStore.getState().user?.id ?? null,
    })
    if (!storeUrls.google || storeUrls.google === '#') {
      e.preventDefault()
    }
  }

  const handleAppStore = (e: React.MouseEvent<HTMLAnchorElement>) => {
    captureSpMobileStoreBannerClicked({
      store_target: 'app_store',
      sp_user_id: useSessionStore.getState().user?.id ?? null,
    })
    if (!storeUrls.apple || storeUrls.apple === '#') {
      e.preventDefault()
    }
  }

  return (
    <Box
      sx={{
        ...homePromoRootSx,
        px: { xs: 3, md: 0 },
        py: { xs: 3, md: 0 },
      }}
      data-testid="home-mobile-promo"
    >
      <Box sx={homePromoBadgeSx} data-testid="home-promo-badge">
        <Typography component="span" sx={homePromoBadgeTextSx}>
          NOW ON MOBILE
        </Typography>
      </Box>

      <Typography component="h2" sx={homePromoHeadingSx}>
        <Box component="span" sx={{ display: 'block' }}>
          Get the full
        </Box>
        <Box component="span" sx={{ display: 'block' }}>
          Brushfire experience
        </Box>
      </Typography>

      <Typography sx={homePromoSubtextSx}>
        More surveys, instant payouts & real-time notifications only on mobile.
      </Typography>

      <Box sx={homePromoStoreRowSx}>
        <Box
          component="a"
          href={storeUrls.google}
          onClick={handleGooglePlay}
          target="_blank"
          rel="noopener noreferrer"
          sx={homePromoGooglePlayLinkSx}
          data-testid="home-promo-google-play"
          aria-label="Get it on Google Play"
        >
          <Box component="img" src={googlePlayBadge} alt="" sx={homePromoGooglePlayImgSx} />
        </Box>
        <Box
          component="a"
          href={storeUrls.apple}
          onClick={handleAppStore}
          target="_blank"
          rel="noopener noreferrer"
          sx={homePromoAppStoreLinkSx}
          data-testid="home-promo-app-store"
          aria-label="Download on the App Store"
        >
          <Box component="img" src={appStoreBadge} alt="" sx={homePromoAppStoreImgSx} />
        </Box>
      </Box>

      <Box sx={homePromoPhoneWrapSx} aria-hidden>
        <Box component="img" src={mobileMockup} alt="" sx={homePromoPhoneImgSx} />
      </Box>

      <Box sx={homePromoFloatSx} data-testid="home-promo-rewards-float">
        <Box sx={homePromoFloatIconSx} aria-hidden>
          <Box
            component="img"
            src={rewardsIcon}
            alt=""
            sx={homePromoFloatIconImgSx}
            data-testid="home-promo-rewards-icon"
          />
        </Box>
        <Box>
          <Typography sx={homePromoRewardsLabelSx}>REWARDS</Typography>
          <Typography sx={homePromoRewardsAmountSx}>+$12.40 Today</Typography>
        </Box>
      </Box>
    </Box>
  )
}
